import { SystemStyleObject } from "@chakra-ui/react";
import {
  InternalAdminUserAttributeCategoryFragment,
  InternalAdminUserAttributeFragment,
} from "gql/graphql";
import React, { memo, useCallback } from "react";
import colors from "app/colors";
import { MultiValue, Select } from "chakra-react-select";
import { CancelIcon } from "modules/icons";
import useQueryString from "modules/hooks/use-query-string";
import useInternalAdminUserAttributeList from "../hooks/use-internal-admin-user-attribute-list";

interface IProps {
  userAttributeCategory: InternalAdminUserAttributeCategoryFragment;
  onSelectedOptionsChanged: (
    options: InternalAdminUserAttributeFragment[],
  ) => void;
  defaultValue?: readonly InternalAdminUserAttributeFragment[];
  placeHolder?: string;
}

const InternalAdminUserAttributesSelector = ({
  userAttributeCategory,
  onSelectedOptionsChanged,
  defaultValue,
  placeHolder = "Select",
}: IProps) => {
  const { qsOrganizationId } = useQueryString();
  const { userAttributes: options } = useInternalAdminUserAttributeList({
    organizationId: qsOrganizationId,
    input: {
      limit: 1000000,
      offset: 0,
      userAttributeCategoryId: userAttributeCategory.id,
    },
  });

  const onChange = (
    userAttributeFragments: MultiValue<InternalAdminUserAttributeFragment>,
  ) => {
    onSelectedOptionsChanged(
      userAttributeFragments as InternalAdminUserAttributeFragment[],
    );
  };

  const getOptionLabel = useCallback(
    (userAttribute: InternalAdminUserAttributeFragment) => userAttribute.name,
    [],
  );

  const getOptionValue = useCallback(
    (userAttribute: InternalAdminUserAttributeFragment) => userAttribute.id,
    [],
  );

  const ClearIndicator = useCallback(
    (props: any) => (
      <CancelIcon
        ref={props.innerRef}
        {...props.innerProps}
        cursor="pointer"
        boxSize={5}
        fill={colors.gray40}
        mr={1}
        _hover={{ opacity: 0.6 }}
      />
    ),
    [],
  );

  return (
    <Select
      isMulti
      isClearable
      options={options}
      defaultValue={defaultValue}
      onChange={onChange}
      placeholder={placeHolder}
      getOptionLabel={getOptionLabel}
      getOptionValue={getOptionValue}
      components={{
        DropdownIndicator: () => null,
        IndicatorSeparator: () => null,
        ClearIndicator,
      }}
      chakraStyles={{
        placeholder: (provided: SystemStyleObject): SystemStyleObject => ({
          ...provided,
          fontStyle: "italic",
          color: colors.placeholderText,
          fontSize: 14,
        }),
        container: (provided: SystemStyleObject): SystemStyleObject => ({
          ...provided,
          width: "full",
          borderColor: colors.grayPrimary,
        }),
        multiValueRemove: (provided: SystemStyleObject): SystemStyleObject => ({
          ...provided,
          color: colors.blue,
        }),
        multiValue: (provided: SystemStyleObject): SystemStyleObject => ({
          ...provided,
          borderRadius: "full",
          borderWidth: "1px",
          borderColor: colors.blue,
          backgroundColor: colors.white,
          color: colors.blue,
        }),
        control: (provided: SystemStyleObject): SystemStyleObject => ({
          ...provided,
          minHeight: "34px",
        }),
      }}
    />
  );
};

export default memo(InternalAdminUserAttributesSelector);
